import type { ReactNode } from 'react';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { LuMegaphone, LuSmartphone, LuPlug, LuWebhook } from 'react-icons/lu';
import { PlataformaEnum, StatusConexaoEnum, type ConexaoPlataforma } from '../../services/ConexaoPlataformaService';
import RdStationSimbolo from '../apps/RdStationSimbolo';

/**
 * O que a tela de Conexões e seus painéis compartilham sobre um canal.
 * Desenho: docs/designsystem/propostas/conexoes.html (A, 30/09/2026).
 */

/** Cor e ícone da marca — as marcas continuam com o ícone da marca (como o
 *  OriginBadge); o resto é Lucide. */
export const classeDaPlataforma = (p: number): string => {
    switch (p) {
        case PlataformaEnum.WhatsappMeta: return 'is-wa';
        case PlataformaEnum.WhatsappEvolution: return 'is-qr';
        case PlataformaEnum.Instagram: return 'is-ig';
        case PlataformaEnum.LeadAds: return 'is-la';
        case PlataformaEnum.RdStation: return 'is-rd';
        case PlataformaEnum.Webhook: return 'is-wh';
        default: return 'is-la';
    }
};

export const iconeDaPlataforma = (p: number, size = 14): ReactNode => {
    switch (p) {
        case PlataformaEnum.WhatsappMeta: return <FaWhatsapp size={size} />;
        case PlataformaEnum.WhatsappEvolution: return <LuSmartphone size={size} />;
        case PlataformaEnum.Instagram: return <FaInstagram size={size} />;
        case PlataformaEnum.LeadAds: return <LuMegaphone size={size} />;
        case PlataformaEnum.RdStation: return <RdStationSimbolo size={size} />;
        case PlataformaEnum.Webhook: return <LuWebhook size={size} />;
        default: return <LuPlug size={size} />;
    }
};

/** Iniciais para o avatar do RecordPanel, que só aceita letras. */
export const iniciaisDaPlataforma = (p: number): string => {
    switch (p) {
        case PlataformaEnum.WhatsappMeta: return 'WA';
        case PlataformaEnum.WhatsappEvolution: return 'QR';
        case PlataformaEnum.Instagram: return 'IG';
        case PlataformaEnum.LeadAds: return 'LA';
        case PlataformaEnum.RdStation: return 'RD';
        case PlataformaEnum.Webhook: return 'WH';
        default: return '?';
    }
};

export const rotuloStatus = (s: number): string => {
    switch (s) {
        case StatusConexaoEnum.Ativa: return 'Ativa';
        case StatusConexaoEnum.Inativa: return 'Inativa';
        case StatusConexaoEnum.Expirada: return 'Expirada';
        default: return '—';
    }
};

export const classeStatus = (s: number): string => {
    switch (s) {
        case StatusConexaoEnum.Ativa: return 'is-ok';
        case StatusConexaoEnum.Inativa: return 'is-off';
        case StatusConexaoEnum.Expirada: return 'is-bad';
        default: return 'is-off';
    }
};

export const formatarData = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR');
};

/** Dias até a data; negativo = já passou; null = sem data. */
export const diasAte = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((d.getTime() - Date.now()) / 86_400_000);
};

/** A 7 dias ou menos do vencimento a data fica em destaque (decisão 4). */
export const DIAS_AVISO_EXPIRACAO = 7;

export interface ConfigDoCanal {
    paginaFacebook?: string;
    usuarioInstagram?: string;
    /** Só Instagram: por qual login da Meta a conta foi conectada. Ausente = Página do Facebook (conexões antigas). */
    loginInstagram?: 'facebook_login' | 'instagram_login';
}

/** `configuracaoJson` guarda coisas diferentes por plataforma; só o que a tela mostra sai daqui. */
export const lerConfig = (c: ConexaoPlataforma): ConfigDoCanal => {
    if (!c.configuracaoJson) return {};
    try {
        const j = JSON.parse(c.configuracaoJson) as Record<string, unknown>;
        return {
            paginaFacebook: (j.facebook_page_name ?? j.page_name) as string | undefined,
            usuarioInstagram: j.instagram_username as string | undefined,
            loginInstagram: c.plataforma === PlataformaEnum.Instagram
                ? (j.tipo_login === 'instagram_login' ? 'instagram_login' : 'facebook_login')
                : undefined,
        };
    } catch {
        return {};
    }
};

/**
 * O que a linha mostra depois do nome da plataforma. No RD e no webhook de entrada o
 * `identificador` é o segredo da URL — nunca vai para a tela (a API já manda mascarado); no
 * lugar dele, o nome da conta RD, ou o que a entrada recebe.
 */
export const identificadorVisivel = (c: ConexaoPlataforma): string => {
    if (c.plataforma === PlataformaEnum.Webhook) return 'recebe leads por URL';
    if (c.plataforma !== PlataformaEnum.RdStation) return c.identificador;
    try {
        const conta = c.configuracaoJson ? (JSON.parse(c.configuracaoJson) as { conta?: string }).conta : undefined;
        return conta || 'conta RD';
    } catch {
        return 'conta RD';
    }
};

/** O token do RD vale 24h e a API renova sozinha: a data de expiração não diz nada ao cliente. */
export const renovaSozinha = (c: ConexaoPlataforma) => c.plataforma === PlataformaEnum.RdStation;

/** Lead Ads, RD Station e webhook de entrada não são canal de conversa: não têm responsável nem agente. */
export const temAtendimento = (c: ConexaoPlataforma) =>
    c.plataforma !== PlataformaEnum.LeadAds && c.plataforma !== PlataformaEnum.RdStation
    && c.plataforma !== PlataformaEnum.Webhook;
